import React from 'react';
import { Info, Edit, Trash2, Merge } from 'lucide-react';
import { LgmDataTableType } from './LgmDataTable';

export interface TableModelCellActions<T = LgmDataTableType> {
  icon: React.ReactNode;
  onAction: (row: T) => void;
  title?: string;
  style?: React.CSSProperties; // Mostly for color
  hoverStyle?: React.CSSProperties; // For hover effects
}

export type TableModelCellProps<T = LgmDataTableType> = {
  row: T;
  onInfo?: (row: T) => void;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  onMerge?: (row: T) => void;
  actions?: TableModelCellActions<T>[];
};

const TableModelCell = <T extends LgmDataTableType>(
  props: TableModelCellProps<T>
) => {
  return (
    <td style={styles.mainTableCellStyle}>
      <div style={styles.actionButtonsContainerStyle}>
        {props.onInfo && (
          <button
            onClick={() => {
              if (props.onInfo) props.onInfo(props.row);
            }}
            style={{
              ...styles.actionButtonStyle,
              ...styles.infoButtonStyle,
            }}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, styles.infoButtonHoverStyle)
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, styles.infoButtonStyle)
            }
            title="Info">
            <Info size={16} />
          </button>
        )}
        {props.onEdit && (
          <button
            onClick={() => {
              if (props.onEdit) props.onEdit(props.row);
            }}
            style={{
              ...styles.actionButtonStyle,
              ...styles.editButtonStyle,
            }}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, styles.editButtonHoverStyle)
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, styles.editButtonStyle)
            }
            title="Edit">
            <Edit size={16} />
          </button>
        )}
        {props.onDelete && (
          <button
            onClick={() => {
              if (props.onDelete) props.onDelete(props.row);
            }}
            style={{
              ...styles.actionButtonStyle,
              ...styles.deleteButtonStyle,
            }}
            onMouseEnter={e =>
              Object.assign(
                e.currentTarget.style,
                styles.deleteButtonHoverStyle
              )
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, styles.deleteButtonStyle)
            }
            title="Delete">
            <Trash2 size={16} />
          </button>
        )}
        {props.onMerge && (
          <button
            onClick={() => {
              if (props.onMerge) props.onMerge(props.row);
            }}
            style={{
              ...styles.actionButtonStyle,
              ...styles.mergeButtonStyle,
            }}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, styles.mergeButtonHoverStyle)
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, styles.mergeButtonStyle)
            }
            title="Merge">
            <Merge size={16} />
          </button>
        )}
        {props.actions &&
          props.actions.map((action, index) => (
            <button
              key={index}
              onClick={() => {
                if (action.onAction) action.onAction(props.row);
              }}
              style={{
                ...styles.actionButtonStyle,
                ...action.style,
              }}
              onMouseEnter={e =>
                Object.assign(e.currentTarget.style, action.hoverStyle || {})
              }
              onMouseLeave={e =>
                Object.assign(e.currentTarget.style, action.style || {})
              }
              title={action.title}>
              {action.icon}
            </button>
          ))}
      </div>
    </td>
  );
};

const styles: Record<string, React.CSSProperties> = {
  mainTableCellStyle: {
    border: '1px solid #d1d5db',
    padding: '8px', // p-2
  } as React.CSSProperties,
  actionButtonsContainerStyle: {
    display: 'flex',
    gap: '8px', // space-x-2
  } as React.CSSProperties,
  actionButtonStyle: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
  } as React.CSSProperties,
  infoButtonStyle: {
    color: '#2563eb', // text-blue-600
  } as React.CSSProperties,
  infoButtonHoverStyle: {
    color: '#1e40af', // hover:text-blue-900 (darker blue)
  } as React.CSSProperties,
  editButtonStyle: {
    color: '#d97706', // text-yellow-600
  } as React.CSSProperties,
  editButtonHoverStyle: {
    color: '#92400e', // hover:text-yellow-800
  } as React.CSSProperties,
  deleteButtonStyle: {
    color: '#dc2626', // text-red-600
  } as React.CSSProperties,
  deleteButtonHoverStyle: {
    color: '#991b1b', // hover:text-red-800
  } as React.CSSProperties,
  mergeButtonStyle: {
    color: '#7c3aed', // text-purple-600
  } as React.CSSProperties,
  mergeButtonHoverStyle: {
    color: '#5b21b6', // hover:text-purple-800
  } as React.CSSProperties,
};

export default TableModelCell;
